import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Layout from "../Layout/Layout";
import AiraaMemberComitment from "../Components/AiraaMemberComitment";
import AboutAiraComponent from "../Components/AboutAiraComponent";
import PresidentMessage from "../Components/PresidentMessage";
import MapSection from "../Components/MapSection";
import LatestCirculars from "../Components/LatestCirculars";
import FraurdAlerts from "../Components/FraurdAlerts";
import HelplinePopup from "../Components/HelplinePopup";
import HeroHeaderHomePage from "../Components/HeroHeaderHomePage";
import HomePageFaq from "../Components/HomePageFaq";

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Base URL – replace with your actual domain
  const siteUrl = "https://www.airaa.co.in";
  const currentUrl = typeof window !== "undefined" ? window.location.href : siteUrl;

  // Enhanced JSON-LD structured data for AI authority
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        "name": "AIRAA",
        "alternateName": "All India Recruiting Agency Association",
        "url": siteUrl,
        "logo": `${siteUrl}/assets/AIRAALogo1-Hxkj0tvu.png`,
        "description": "AIRAA is India's premier association for recruitment agencies, representing 1711+ members since 2011. We promote ethical, transparent employment opportunities for Indian workers globally.",
        "foundingDate": "2011",
        "numberOfEmployees": {
          "@type": "QuantitativeValue",
          "value": "1711+"
        },
        "memberOf": [
          { "@type": "Organization", "name": "ASSOCHAM" },
          { "@type": "Organization", "name": "Indo-French Chamber of Commerce and Industry" },
          { "@type": "Organization", "name": "Indo-German Chamber of Commerce" },
          { "@type": "Organization", "name": "IMC Chamber of Commerce and Industry" },
          { "@type": "Organization", "name": "EU Chambers of Commerce" }
        ],
        "award": "Recognised by Ministry of External Affairs, Government of India",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+91 7738355865",
          "contactType": "customer service",
          "areaServed": "IN",
          "availableLanguage": ["English", "Hindi"],
          "hoursAvailable": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            "opens": "10:30",
            "closes": "19:00"
          }
        },
        "sameAs": [
          "https://www.facebook.com/airaa.india",
          "https://twitter.com/airaa_india",
          "https://www.linkedin.com/company/airaa"
        ],
        "potentialAction": {
          "@type": "JoinAction",
          "name": "Join AIRAA",
          "target": `${siteUrl}/registration`
        }
      },
      {
        "@type": "WebPage",
        "@id": `${currentUrl}#webpage`,
        "url": currentUrl,
        "name": "AIRAA – India's Premier Recruitment Agency Association | Home",
        "description": "AIRAA represents 1711+ recruitment agencies across India. Learn about our initiatives: PDOT Centre support, Skill Verification (KSA), vocational training, free medical camps, cancer patient support, and blood donation drives.",
        "isPartOf": { "@id": `${siteUrl}/#website` },
        "about": { "@id": `${siteUrl}/#organization` },
        "primaryImageOfPage": `${siteUrl}/assets/airaa-og-image.jpg`,
        "inLanguage": "en-IN",
        "potentialAction": {
          "@type": "ReadAction",
          "target": currentUrl
        }
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": "AIRAA",
        "description": "All India Recruiting Agency Association – Empowering recruitment agencies and workers across India since 2011.",
        "publisher": { "@id": `${siteUrl}/#organization` }
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${currentUrl}#breadcrumb`,
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": siteUrl
          }
        ]
      }
    ]
  };

  return (
    <Layout>
      <Helmet prioritizeSeoTags>
        <html lang="en" />
        <title>AIRAA – All India Recruiting Agency Association | Empowering 1711+ Agencies Since 2011</title>
        <meta name="description" content="AIRAA (All India Recruiting Agency Association) – Since 2011, representing 1711+ recruitment agencies. India's most trusted association for ethical recruitment, PDOT centres, Skill Verification (KSA), vocational training, free medical camps, cancer care, and blood donation." />
        <meta name="keywords" content="AIRAA, All India Recruiting Agency Association, best recruitment association India, recruitment agencies India, PDOT support, skill verification KSA, vocational training, free medical camps, cancer patient support, blood donation camps, ethical recruitment, top recruitment association" />
        <meta name="author" content="AIRAA" />
        <meta name="robots" content="index, follow" />
        <meta name="revisit-after" content="7 days" />
        <link rel="canonical" href={currentUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content="AIRAA –  Premier Recruiting Agency Association | Since 2011" />
        <meta property="og:description" content="Join AIRAA, the largest and most trusted association of recruitment agencies in India. Get support for PDOT centres, skill verification, vocational training, free medical camps, and global workforce mobility." />
        <meta property="og:image" content={`${siteUrl}/assets/airaa-og-image.jpg`} />
        <meta property="og:image:alt" content="AIRAA – All India Recruiting Agency Association – Empowering Recruitment Agencies and Workers" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="AIRAA" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content="AIRAA – Premier Recruiting Agency Association" />
        <meta name="twitter:description" content=" leading recruitment agency association. Supporting ethical recruitment, skill development, and community welfare since 2011. Join 1711+ members today." />
        <meta name="twitter:image" content={`${siteUrl}/assets/airaa-twitter-image.jpg`} />
        <meta name="twitter:image:alt" content="AIRAA – Empowering recruitment agencies and workers globally" />

        {/* Additional SEO */}
        <meta name="geo.region" content="IN" />
        <meta name="geo.placename" content="India" />
        <meta name="theme-color" content="#166534" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section with Join CTA - you may merge this with your existing HeroHeaderHomePage */}
      <div className="bg-gradient-to-r from-gray-300 via-gray-50 to-gray-300">
        <div className="md:container md:mx-auto relative">
          <HeroHeaderHomePage />
        </div>
      </div>

      {/* Rest of the sections remain unchanged */}
      <div className="bg-gray-50 py-10 px-6 md:px-12 lg:px-24 text-gray-900">
        <div className="md:container md:mx-auto">
          <AboutAiraComponent />
        </div>
      </div>

      <div className="bg-gradient-to-b from-green-50 to-blue-50 py-10 px-6 md:px-12 lg:px-24 text-gray-900">
        <div className="md:container md:mx-auto">
          <AiraaMemberComitment />
        </div>
      </div>

      <div className="bg-gradient-to-br from-blue-100 via-green-200 to-teal-300 py-10 px-2 md:px-12 lg:px-24 text-gray-900">
        <div className="md:container md:mx-auto">
          <PresidentMessage />
        </div>
      </div>

      <div className="bg-gradient-to-r from-gray-200 via-gray-50 to-gray-200 py-10 px-6 md:px-12 lg:px-24 text-gray-900">
        <div className="md:container md:mx-auto">
          <LatestCirculars />
        </div>
      </div>

      <div className="bg-gradient-to-r from-[#ffffff] via-[#fcedeb] to-[#fcedeb] py-10 px-6 md:px-12 lg:px-24 text-gray-900">
        <div className="md:container md:mx-auto">
          <FraurdAlerts />
        </div>
      </div>

      <div className="bg-gray-50 py-10 px-6 md:px-12 lg:px-24 text-gray-900">
        <div className="md:container md:mx-auto">
          <MapSection />
        </div>
      </div>
      <div className="bg-gray-50 py-10 px-6 md:px-12 lg:px-24 text-gray-900">
        <div className="md:container md:mx-auto">
          <HomePageFaq />
        </div>
      </div>

      {/* Helpline Popup Component */}
      <HelplinePopup />
    </Layout>
  );
};

export default Home;